import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import type { VarianceReport, VarianceRow } from '../../types';
import { Mono } from '@/shared/ui/atoms/Mono';
import { DataTable, type Column } from '@/shared/ui/organisms/DataTable';
import { formatMinutes } from '@/shared/utils/time';
import { varianceTone } from '@/shared/utils/variance';
import './VarianceTable.styl';

type Props = { report: VarianceReport; sprintLabel?: string | undefined; onOpenTask?: ((taskId: string) => void) | undefined };

export function Variance({ minutes }: { minutes: number | null }) {
  const tone = varianceTone(minutes);
  return <Mono className={`f-variance__delta f-variance__delta--${tone}`}>{minutes === null ? '—' : formatMinutes(minutes, { signed: true })}</Mono>;
}

const buildColumns = (t: TFunction): Column<VarianceRow>[] => [
  { key: 'task', header: t('insights.variance.task'), width: '34%', render: (r) => <span className="f-variance__task">{r.task}</span> },
  { key: 'owner', header: t('insights.variance.owner'), width: '20%', render: (r) => <span className="f-variance__owner">{r.owner}</span> },
  { key: 'tag', header: t('insights.variance.tag'), render: (r) => (r.tag ? <span className="f-variance__tag">{r.tag}</span> : <span className="f-variance__muted">—</span>) },
  { key: 'estimateMinutes', header: t('insights.variance.est'), render: (r) => <Mono className="f-variance__est">{formatMinutes(r.estimateMinutes)}</Mono> },
  { key: 'loggedMinutes', header: t('insights.variance.logged'), mono: true, render: (r) => formatMinutes(r.loggedMinutes) },
  { key: 'varianceMinutes', header: t('insights.variance.variance'), render: (r) => <Variance minutes={r.varianceMinutes} /> }
];

export function VarianceTable({ report, sprintLabel, onOpenTask }: Props) {
  const { t } = useTranslation();
  const columns = buildColumns(t);
  const logged = report.rows.filter((r) => r.loggedMinutes > 0).length;
  return (
    <div className="f-variance">
      <DataTable
        columns={columns}
        rows={report.rows}
        rowKey={(r) => r.taskId}
        empty={t('insights.variance.empty')}
        {...(onOpenTask ? { onRowClick: (r: VarianceRow) => onOpenTask(r.taskId) } : {})}
        footer={
          <tr>
            <td colSpan={3}>{t('insights.variance.total')}</td>
            <td>
              <Mono>{formatMinutes(report.totalEstimate)}</Mono>
            </td>
            <td>
              <Mono>{formatMinutes(report.totalLogged)}</Mono>
            </td>
            <td>
              <Variance minutes={logged === 0 ? null : report.totalVariance} />
            </td>
          </tr>
        }
      />
      <div className="f-variance__foot">
        <span>
          {t('insights.variance.showing', { count: report.rows.length })}
          {sprintLabel ? ` · ${sprintLabel}` : ''} · {t('insights.variance.withLogged', { count: logged })}
        </span>
      </div>
    </div>
  );
}
